
import React, {
   useState,
   useEffect,
   useImperativeHandle,
   forwardRef
} from "react";
import { useDispatch } from "react-redux";
import { useDropzone } from "react-dropzone";
import Swal from "sweetalert2";
import {
   FiUploadCloud,
   FiTrash2,
   FiLoader,
   FiCheckCircle,
   FiAlertCircle,
   FiFileText,
   FiInfo
} from "react-icons/fi";
import { FaRegFilePdf } from "react-icons/fa";
import { uploadEvidence } from "../../store/Thunk";
// import { uploadEvidence } from "../../store/thunks/evidences"; // ajusta ruta


export const Uploader = forwardRef(({
   entityType,
   entityId = null,
   tempKey = null,
   userRegister,
   oldFile,
   onUploadStateChange
}, ref) => {

   const dispatch = useDispatch();
   const [queueFiles, setQueueFiles] = useState([]);
   const [loading, setLoading] = useState(false);

   /* ================================
      Dropzone
   ================================== */

   const onDrop = (acceptedFiles) => {

      if (loading) return;

      const mapped = acceptedFiles.map(file => ({
         id: crypto.randomUUID(),
         file,
         preview: URL.createObjectURL(file),
         status: "pending",
         error: null
      }));

      setQueueFiles(prev => [...prev, ...mapped]);
   };

   const {
      getRootProps,
      getInputProps,
      isDragActive,
      fileRejections
   } = useDropzone({
      onDrop,
      multiple: true,
      maxSize: 10 * 1024 * 1024
   });

   /* ================================
      Helpers
   ================================== */

   const updateStatus = (id, status, error = null) => {
      setQueueFiles(prev =>
         prev.map(f =>
            f.id === id ? { ...f, status, error } : f
         )
      );
   };

   const removeFromQueue = (id) => {
      if (loading) return;

      setQueueFiles(prev => {
         const fileToRemove = prev.find(f => f.id === id);
         if (fileToRemove) {
            URL.revokeObjectURL(fileToRemove.preview);
         }
         return prev.filter(f => f.id !== id);
      });
   };

   const openFile = (file) => {
      const url = URL.createObjectURL(file);
      window.open(url, "_blank");
   };

   /* ================================
      Upload Logic
   ================================== */
   const uploadFiles = async () => {

      if (!entityId && !tempKey) {
         Swal.fire({
            icon: "error",
            title: "Referencia inválida",
            text: "No existe entidad ni referencia temporal."
         });
         return { ok: false, successCount: 0 };
      }

      let successCount = 0;
      let hasError = false;

      setLoading(true);

      for (const f of queueFiles) {

         if (f.status === "success") {
            successCount++;
            continue;
         }

         updateStatus(f.id, "uploading");

         const res = await dispatch(uploadEvidence(
            entityId ? 0 : tempKey,
            // tempKey,
            userRegister,
            f,
            {
               module: "BILLINGS",
               entity_type: entityType,
               entity_id: entityId ?? 0
            }
         ));

         if (res?.ok === true) {
            updateStatus(f.id, "success");
            successCount++;
         } else {
            updateStatus(f.id, "error", res?.error ?? "Error desconocido");
            hasError = true;
         }
      }

      setLoading(false);

      return {
         ok: !hasError,
         successCount
      };
   };

   const retryFailed = async () => {
      if (loading) return;
      await uploadFiles();
   };

   const confirmUpload = async () => {
      await uploadFiles();
   };

   /* ================================
      Expose to Parent
   ================================== */

   useImperativeHandle(ref, () => ({
      uploadAll: async () => await uploadFiles(),
      hasPending: () => queueFiles.some(f => f.status !== "success"),
      hasErrors: () => queueFiles.some(f => f.status === "error"),
      getSuccessCount: () => queueFiles.filter(f => f.status === "success").length
   }));

   /* ================================
      Notify Parent
   ================================== */

   useEffect(() => {
      const successCount = queueFiles.filter(f => f.status === "success").length;
      if (onUploadStateChange) {
         onUploadStateChange(successCount);
      }
   }, [queueFiles]);

   /* ================================
      Cleanup
   ================================== */

   useEffect(() => {
      return () => {
         queueFiles.forEach(f => URL.revokeObjectURL(f.preview));
      };
   }, []);

   const hasFailedFiles = queueFiles.some(f => f.status === "error");

   /* ================================
      UI
   ================================== */

   return (
      <div className=''>

         <div className="card-header border-bottom">
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2">

               <h4 className="fw-bold mb-0">
                  Adjuntos
                  {queueFiles.length > 0 && (
                     <span className="badge bg-secondary ms-2">
                        {queueFiles.filter(f => f.status === 'success').length} / {queueFiles.length}
                     </span>
                  )}
                  {!entityId && (
                     <span className="badge bg-warning ms-2">
                        {/* Modo Temporal */}
                     </span>
                  )}
               </h4>

               <div className="btn-group">
                  <button
                     type='button'
                     className="btn btn-outline-primary me-2"
                     onClick={confirmUpload}
                     disabled                  >
                     <FiUploadCloud size={20} />
                     {loading && <span className="ms-2">Subiendo...</span>}
                  </button>

                  <button
                     type='button'
                     className="btn btn-outline-danger"
                     onClick={() => setQueueFiles([])}
                     disabled={loading || queueFiles.length === 0}
                  >
                     <FiTrash2 size={20} />
                  </button>

                  {hasFailedFiles && (
                     <button
                        type='button'
                        className="btn btn-outline-warning"
                        onClick={retryFailed}
                        disabled={loading}
                     >
                        Reintentar ({queueFiles.filter(f => f.status === 'error').length})
                     </button>
                  )}
               </div>
            </div>
         </div>

         <div className='card-body'>

            <div
               {...getRootProps()}
               className={`border rounded p-4 text-center mb-3 ${isDragActive ? 'border-primary bg-light' : ''} ${loading ? 'opacity-50' : ''}`}
               style={{ cursor: loading ? 'not-allowed' : 'pointer' }}
            >
               <input {...getInputProps()} />
               <h6 className="fw-bold mb-0">
                  {isDragActive
                     ? 'Suelta los archivos aquí...'
                     : 'Arrastra archivos o haz clic para seleccionar'}
               </h6>
            </div>

            {fileRejections.length > 0 && (
               <div className="alert alert-danger">
                  {fileRejections.map(({ file, errors }) => (
                     <div key={file.name}>
                        <strong>{file.name}</strong>
                        <ul className="mb-0">
                           {errors.map(e => (
                              <li key={e.code}>{e.message}</li>
                           ))}
                        </ul>
                     </div>
                  ))}
               </div>
            )}

            <div className="mt-1" style={{ maxHeight: "190px", overflowY: "auto", overflowX: "hidden", }}>
               <div className="row">

                  {queueFiles.length === 0 && (
                     <span className="text-start fw-bold text-muted mt-3">
                        <FiInfo size={18} /> No hay archivos
                     </span>
                  )}

                  {queueFiles.map((f) => (
                     <div key={f.id} className="col-12 col-sm-12 col-md-6 col-lg-6 col-xl-6 col-xxl-6 mb-2 border-bottom ">

                        <div className="p-2">

                           <div className="d-flex justify-content-between align-items-center">

                              <div onClick={() => openFile(f.file)} style={{ cursor: "pointer" }}>
                                 {f.file.type.startsWith("image/") ? (
                                    <img
                                       src={f.preview}
                                       alt="preview"
                                       className="rounded"
                                       style={{ width: 40, height: 40, objectFit: "cover" }}
                                    />
                                 ) : f.file.type === "application/pdf" ? (
                                    <FaRegFilePdf size={40} className="text-danger" />
                                 ) : (
                                    <FiFileText size={40} />
                                 )}
                              </div>

                              <button
                                 type="button"
                                 className="btn btn-sm btn-outline-danger"
                                 onClick={() => removeFromQueue(f.id)}
                                 disabled={loading}
                              >
                                 <FiTrash2 size={18} />
                              </button>

                           </div>

                           <div className="mt-2">
                              <strong className="d-block text-truncate">{f.file.name}</strong>
                              <small className="text-muted">
                                 {(f.file.size / 1024 / 1024).toFixed(2)} MB
                              </small>
                           </div>

                           <div className="mt-2">
                              {f.status === 'pending' && <span className="badge bg-dark">Pendiente</span>}
                              {f.status === 'uploading' && <span className="badge bg-info">Subiendo...</span>}
                              {f.status === 'success' && <span className="badge bg-success">Subido</span>}
                              {f.status === 'error' && <span className="badge bg-danger">Error</span>}
                           </div>

                        </div>
                     </div>
                  ))}
               </div>
            </div>

         </div>
      </div>
   );
});